import React, { useEffect, useState } from 'react';
import Toast from 'react-native-toast-message';
import { translate } from '../../../services/translate';
import { View, NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { accessService } from '../../../services/central-api/access';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { useAuth } from '../../../context/Auth';
import FilterInput from '../../../components/Filter/components/FilterInput';
import { AccessParamList } from '../../../typings/Params';
import useFilters from '../../../components/Filter/hooks/useFilters';
import CustomButton from '../../../components/CustomButton';
import MyDataTable from '../../../components/MyDataTable';
import DropdownButton from '../../../components/DropdownButton';
import { Pagination } from '../../../components/Pagination';
import UserTagModal from './UserTagModal';
import DeleteModal from '../../../components/DeleteModal';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function UserTags({ navigation }: { navigation: Navigation; }) {
    const { user: authenticatedUser } = useAuth();

    const [tags, setTags] = useState<UserTag[]>([]);

    const route = useRoute<RouteProp<AccessParamList, 'UserTags'>>();
    const [filters, setFilters] = useFilters<AccessParamList['UserTags']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [totalItems, setTotalItems] = useState(0);
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [selectedTag, setSelectedTag] = useState<UserTag>();

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessUserTags', filters],
        queryFn: () => accessService.getUserTagsPaginated(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setTags(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function deleteTag(tag: UserTag) {
        try {
            await accessService.deleteUserTag(tag.id);
        } catch (error) {
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    function handleFormClose() {
        setSelectedTag(undefined);
        setModalVisible(false);
        refetch();
    }

    return (
        <Container>
            <Header
                title={translate('userTags')}
                actions={<>
                    {(authenticatedUser?.isAdmin || authenticatedUser?.permissions.access_management_user_tags) &&
                        <CustomButton text={translate('register')} onPress={() => {
                            setSelectedTag(undefined);
                            setModalVisible(true);
                        }} />
                    }
                </>}
            />
            <Card>
                <DataTableHeader
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={text => {
                            setTextFilter(text);
                        }}
                        onKeyPress={handleKeyDown}
                    />}
                    title={translate('registeredUserTags')}
                    zIndex={2}
                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[{
                            name: 'ID',
                            selector: row => row.id ?? '',
                            grow: 0
                        },
                        {
                            name: translate('color'),
                            grow: 0,
                            cell: row => <View style={{ backgroundColor: row.color, height: 20, width: 20, borderRadius: 4 }}></View>
                        },
                        {
                            name: translate('name'),
                            selector: row => row.name,
                            wrap: true,
                            grow: 4
                        },
                        {
                            name: translate('associatedUsers'),
                            selector: row => row.associatedCount ?? '',
                            wrap: true,
                        },
                        {
                            cell: row => <DropdownButton
                                actions={[{
                                    content: translate('editInformation'),
                                    onPress: () => {
                                        setSelectedTag(row);
                                        setModalVisible(true);
                                    }
                                }, {
                                    content: translate('delete'),
                                    variant: 'danger',
                                    onPress: () => {
                                        setSelectedTag(row);
                                        setIsDeleteModalVisible(true);
                                    },
                                }]}
                            />,
                            wrap: true,
                            button: true
                        }]}
                        data={tags}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                    />}
                />
            </Card>
            <UserTagModal
                isModalVisible={isModalVisible}
                setModalVisible={setModalVisible}
                selectedTag={selectedTag}
                onClose={handleFormClose}
            />
            <DeleteModal onSubmit={async () => {
                try {
                    if (selectedTag) {
                        await deleteTag(selectedTag);
                        await refetch();
                    }
                } catch (err) {
                    console.error(err);
                }
            }} isModalVisible={isDeleteModalVisible} itemName={selectedTag?.name} setModalVisible={() => setIsDeleteModalVisible(false)} />
        </Container>
    );
}
